import { Dispatch, SetStateAction, useEffect, useMemo } from "react";

import { useGetSomeIngredientReferencesUnified } from "src/api/client-api/ingredientReferenceUnified/useGetSomeIngredientReferencesUnified";
import type { ProductFormProductIngredient, ProductFormType } from "src/pages/Product/ProductForm/ProductForm.types";

import { collectIngredientReferenceCompositeIds, getIngredientReferenceCompositeId } from "../ecobalyseMass";
import { resolveIngredientOriginDefault } from "./resolveIngredientOrigin";

const enrichIngredientWithReference = (
    productIngredient: ProductFormProductIngredient,
    reference: NonNullable<ReturnType<typeof useGetSomeIngredientReferencesUnified>["ingredientReferences"]>[number],
): ProductFormProductIngredient => {
    const compositeId = getIngredientReferenceCompositeId(productIngredient) ?? reference.compositeId;
    const defaultOrigin = reference.defaultOrigin ?? null;
    const nextOrigin = productIngredient.origin ?? defaultOrigin ?? resolveIngredientOriginDefault(productIngredient);

    if (productIngredient.ingredientReference) {
        return {
            ...productIngredient,
            origin: nextOrigin,
            ingredientReference: {
                ...productIngredient.ingredientReference,
                compositeId,
                id: reference.id,
                source: reference.source,
                name: reference.name,
                defaultOrigin,
                fresh: reference.fresh ?? productIngredient.ingredientReference.fresh,
                nonEdiblePartRatio:
                    reference.nonEdiblePartRatio ?? productIngredient.ingredientReference.nonEdiblePartRatio,
                rawToCookedRatio: reference.rawToCookedRatio ?? productIngredient.ingredientReference.rawToCookedRatio,
            },
        };
    }

    if (productIngredient.ingredient?.ingredientReferenceId !== undefined) {
        return {
            ...productIngredient,
            origin: nextOrigin,
            ingredientReference: {
                compositeId,
                id: reference.id,
                source: reference.source,
                name: reference.name,
                defaultOrigin,
                fresh: reference.fresh,
                nonEdiblePartRatio: reference.nonEdiblePartRatio,
                rawToCookedRatio: reference.rawToCookedRatio,
            },
        };
    }

    return productIngredient;
};

const areSameIngredientOrigins = (left: ProductFormProductIngredient, right: ProductFormProductIngredient): boolean =>
    left.origin === right.origin &&
    left.ingredientReference?.defaultOrigin === right.ingredientReference?.defaultOrigin &&
    left.ingredientReference?.compositeId === right.ingredientReference?.compositeId;

export const useHydrateIngredientOrigins = (
    productIngredients: ProductFormProductIngredient[],
    setProduct: Dispatch<SetStateAction<ProductFormType>>,
) => {
    const compositeIds = useMemo(
        () => collectIngredientReferenceCompositeIds(productIngredients),
        [productIngredients],
    );
    const { ingredientReferences } = useGetSomeIngredientReferencesUnified(compositeIds);

    useEffect(() => {
        if (ingredientReferences === undefined || ingredientReferences.length === 0) {
            return;
        }

        setProduct((currentProduct) => {
            let changed = false;

            const nextIngredients = currentProduct.productIngredients.map((productIngredient) => {
                const compositeId = getIngredientReferenceCompositeId(productIngredient);
                if (compositeId === null) {
                    return productIngredient;
                }

                const reference = ingredientReferences.find((item) => item.compositeId === compositeId);
                if (reference === undefined) {
                    return productIngredient;
                }

                const enriched = enrichIngredientWithReference(productIngredient, reference);
                if (!areSameIngredientOrigins(productIngredient, enriched)) {
                    changed = true;
                }

                return enriched;
            });

            if (!changed) {
                return currentProduct;
            }

            return {
                ...currentProduct,
                productIngredients: nextIngredients,
            };
        });
    }, [ingredientReferences, setProduct]);
};

export const resolveSelectedIngredientOrigin = (
    ingredient: ProductFormProductIngredient,
): ProductFormProductIngredient => ({
    ...ingredient,
    origin: ingredient.origin ?? ingredient.ingredientReference?.defaultOrigin ?? null,
});
